// Constraint "density matrix" colour encoding (Resources page + detail drawer).
//
// A Constraint rating carries two dimensions of meaning that we encode visually:
//   • hue       = which Constraint group it belongs to (Domain / Platform / Offering / ICP)
//   • intensity = the RM-confirmed strength, 1 (light) → 5 (deep)
//
// This is a structural read, NOT a Fit Score read — so it deliberately does NOT use
// the red/amber/green --fit-* status colours. The group hues reuse the brand chart
// ramp (cyan → blue → indigo → purple) so the page stays on-palette in both themes.
// Single source of truth: both the table cells and the drawer matrix read from here.

export const CONSTRAINT_GROUP_ORDER = ["Domain", "Platform", "Offering", "ICP"] as const;

// Group → base hue, via the --constraint-* product tokens (design-system/tokens.css).
// Falls back to slate for any group outside the canonical four.
const GROUP_HUE: Record<string, string> = {
  Domain: "var(--constraint-domain)",
  Platform: "var(--constraint-platform)",
  Offering: "var(--constraint-offering)",
  ICP: "var(--constraint-icp)",
};

const FALLBACK_HUE = "var(--constraint-fallback)";

export function constraintGroupHue(group: string): string {
  return GROUP_HUE[group] ?? FALLBACK_HUE;
}

// Rating 1→5 mapped to alpha (light → deep). Unrated (null) renders as an empty
// hairline cell so absence reads differently from a low rating.
const RATING_ALPHA: Record<number, number> = {
  1: 0.22,
  2: 0.4,
  3: 0.58,
  4: 0.78,
  5: 1,
};

// Background for a single density cell. null rating → transparent fill (caller
// supplies the hairline border); rated → group hue at the rating's intensity.
// color-mix (not a hex alpha suffix) because the hue is a CSS variable.
export function constraintCellBackground(group: string, rating: number | null): string {
  if (rating == null) return "transparent";
  const alpha = RATING_ALPHA[rating] ?? RATING_ALPHA[5] ?? 1;
  return `color-mix(in srgb, ${constraintGroupHue(group)} ${Math.round(alpha * 100)}%, transparent)`;
}
